import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Trash2, Plus, Minus, Truck, Store, MapPin, CreditCard } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Cart = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, getSubtotal, clearCart } = useCart();
  const { user, token, isAuthenticated } = useAuth();
  
  const [zones, setZones] = useState([]);
  const [deliveryType, setDeliveryType] = useState('pickup');
  const [selectedZone, setSelectedZone] = useState('');
  const [address, setAddress] = useState('');
  const [usePoints, setUsePoints] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('mercadopago'); // Default to Mercado Pago
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API}/zones`);
      setZones(response.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  const getShippingCost = () => {
    if (deliveryType === 'pickup') return 0;
    const zone = zones.find(z => z.id === selectedZone);
    return zone ? zone.shipping_cost : 0;
  };

  const getDiscount = () => {
    if (!usePoints || !user || user.points < 100) return 0;
    return getSubtotal() * 0.20;
  };

  const getTotal = () => {
    return getSubtotal() - getDiscount() + getShippingCost();
  };

  const getPointsToEarn = () => {
    return Math.floor((getSubtotal() / 110) * 10);
  };

  const getPickupTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    
    // Format time in 12-hour format with AM/PM
    let hours = now.getHours();
    const minutes = now.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    
    return `${hours}:${minutesStr} ${ampm}`;
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para continuar');
      navigate('/login');
      return;
    }

    if (items.length === 0) {
      toast.error('Tu carrito está vacío');
      return;
    }

    if (deliveryType === 'delivery') {
      if (!selectedZone) {
        toast.error('Selecciona una zona de envío');
        return;
      }
      if (!address.trim()) {
        toast.error('Ingresa tu dirección');
        return;
      }
    }

    if (deliveryType === 'mercadolibre') {
      if (!address.trim()) {
        toast.error('Ingresa tu dirección para Mercado Envíos');
        return;
      }
    }

    setLoading(true);
    try {
      // Create order
      const orderResponse = await axios.post(`${API}/orders`, {
        items: items.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        })),
        delivery_type: deliveryType,
        zone_id: deliveryType === 'delivery' ? selectedZone : null,
        address: deliveryType === 'delivery' ? address : null,
        use_points: usePoints && user?.points >= 100,
        payment_method: paymentMethod === 'cash' ? 'cash' : 'online'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const order = orderResponse.data;

      // If cash payment, show success and clear cart
      if (paymentMethod === 'cash') {
        clearCart();
        toast.success('¡Pedido creado! Te esperamos en la farmacia.');
        navigate('/dashboard?tab=orders');
        return;
      }

      // Create checkout session based on payment method
      if (paymentMethod === 'mercadopago') {
        const mpResponse = await axios.post(`${API}/checkout/mercadopago/create-preference`, {
          order_id: order.id,
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Redirect to Mercado Pago (production URL)
        const redirectUrl = mpResponse.data.checkout_url;
        window.location.href = redirectUrl;
      } else {
        // Stripe checkout
        const checkoutResponse = await axios.post(`${API}/checkout/create-session`, {
          order_id: order.id,
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Redirect to Stripe
        window.location.href = checkoutResponse.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el pedido');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 py-16">
        <div className="container-app text-center">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingCart className="w-12 h-12 text-slate-400" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Tu carrito está vacío</h1>
          <p className="text-slate-500 mb-6">Agrega productos para continuar</p>
          <Button onClick={() => navigate('/productos')} className="btn-primary">
            Ver Productos
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container-app">
        <h1 className="text-3xl font-bold text-slate-900 mb-8">Tu Carrito</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => {
              const price = item.product.discount_percent
                ? item.product.price * (1 - item.product.discount_percent / 100)
                : item.product.price;

              return (
                <div 
                  key={item.product.id} 
                  className="bg-white rounded-2xl p-4 flex gap-4"
                  data-testid={`cart-item-${item.product.id}`}
                >
                  <img
                    src={item.product.image_url || 'https://via.placeholder.com/100'}
                    alt={item.product.name}
                    className="w-24 h-24 rounded-xl object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.product.name}</h3>
                        <p className="text-sm text-slate-500">{item.product.category}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(item.product.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`remove-item-${item.product.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="w-8 h-8"
                          data-testid={`decrease-${item.product.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="w-8 h-8"
                          data-testid={`increase-${item.product.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-slate-900">
                          ${(price * item.quantity).toFixed(2)}
                        </p>
                        {item.product.discount_percent && (
                          <p className="text-xs text-slate-400 line-through">
                            ${(item.product.price * item.quantity).toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="space-y-4">
            {/* Delivery Options */}
            <div className="bg-white rounded-2xl p-6">
              <h2 className="font-semibold text-slate-900 mb-4">Método de Entrega</h2>
              <RadioGroup value={deliveryType} onValueChange={setDeliveryType}>
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 mb-2">
                  <RadioGroupItem value="pickup" id="pickup" />
                  <Label htmlFor="pickup" className="flex items-center gap-2 cursor-pointer flex-1">
                    <Store className="w-5 h-5 text-[#2E7DCD]" />
                    <div>
                      <p className="font-medium">Recoger en Tienda</p>
                      <p className="text-xs text-slate-500">Gratis</p>
                    </div>
                  </Label>
                </div>
                
                {/* Pickup time estimate */}
                {deliveryType === 'pickup' && (
                  <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200 animate-fadeIn">
                    <div className="flex items-center gap-2 text-blue-700">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="font-medium text-sm">Tu pedido estará listo en aproximadamente 1 hora</p>
                        <p className="text-xs text-blue-600">
                          Hora estimada de recogida: <strong>{getPickupTime()}</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 mb-2">
                  <RadioGroupItem value="delivery" id="delivery" />
                  <Label htmlFor="delivery" className="flex items-center gap-2 cursor-pointer flex-1">
                    <Truck className="w-5 h-5 text-[#2E7DCD]" />
                    <div>
                      <p className="font-medium">Envío a Domicilio</p>
                      <p className="text-xs text-slate-500">Costo según zona</p>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg border-2 border-[#FFE600] bg-[#FFF9E6]">
                  <RadioGroupItem value="mercadolibre" id="mercadolibre" />
                  <Label htmlFor="mercadolibre" className="flex items-center gap-2 cursor-pointer flex-1">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                      <rect width="24" height="24" rx="4" fill="#FFE600"/>
                      <path d="M12 4C7.58 4 4 7.58 4 12s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14.4c-3.53 0-6.4-2.87-6.4-6.4S8.47 5.6 12 5.6s6.4 2.87 6.4 6.4-2.87 6.4-6.4 6.4z" fill="#2D3277"/>
                      <path d="M14.8 9.6c-.44-.44-1.04-.69-1.67-.69-.63 0-1.23.25-1.67.69l-.13.13-.13-.13c-.44-.44-1.04-.69-1.67-.69-.63 0-1.23.25-1.67.69-.92.92-.92 2.42 0 3.35L12 17.2l4.13-4.25c.93-.93.93-2.43.01-3.35z" fill="#2D3277"/>
                    </svg>
                    <div>
                      <p className="font-medium">Mercado Libre</p>
                      <p className="text-xs text-slate-600">Envío con Mercado Envíos</p>
                    </div>
                  </Label>
                </div>
              </RadioGroup>

              {deliveryType === 'delivery' && (
                <div className="mt-4 space-y-4 animate-fadeIn">
                  <div>
                    <Label>Zona de Envío</Label>
                    <Select value={selectedZone} onValueChange={setSelectedZone}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Selecciona tu zona" />
                      </SelectTrigger>
                      <SelectContent>
                        {zones.map((zone) => (
                          <SelectItem key={zone.id} value={zone.id}>
                            {zone.name} - ${zone.shipping_cost.toFixed(2)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Dirección</Label>
                    <Input
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Calle, número, colonia..."
                      className="mt-1"
                      data-testid="address-input"
                    />
                  </div>
                </div>
              )}

              {deliveryType === 'mercadolibre' && (
                <div className="mt-4 p-4 bg-[#FFF9E6] rounded-lg border border-[#FFE600]">
                  <p className="text-sm text-slate-700">
                    El envío se procesará a través de <strong>Mercado Envíos</strong>.
                    Recibirás el costo de envío al finalizar la compra.
                  </p>
                  <div className="mt-3">
                    <Label>Dirección de envío</Label>
                    <Input
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Calle, número, colonia, código postal..."
                      className="mt-1 bg-white"
                      data-testid="ml-address-input"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Points */}
            {isAuthenticated && user?.points >= 100 && (
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Usar 100 puntos</p>
                    <p className="text-sm text-[#E52225]">20% de descuento</p>
                    <p className="text-xs text-slate-500">Tienes {user.points} puntos</p>
                  </div>
                  <Switch
                    checked={usePoints}
                    onCheckedChange={setUsePoints}
                    data-testid="use-points-toggle"
                  />
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="bg-white rounded-2xl p-6">
              <h2 className="font-semibold text-slate-900 mb-4">Resumen</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="text-slate-900">${getSubtotal().toFixed(2)}</span>
                </div>
                {getDiscount() > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Descuento (20%)</span>
                    <span>-${getDiscount().toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Envío</span>
                  <span className="text-slate-900">
                    {getShippingCost() === 0 ? 'Gratis' : `$${getShippingCost().toFixed(2)}`}
                  </span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-[#E52225]">${getTotal().toFixed(2)}</span>
                  </div>
                </div>
                {isAuthenticated && (
                  <p className="text-xs text-green-600 mt-2">
                    +{getPointsToEarn()} puntos con esta compra
                  </p>
                )}
              </div>

              {/* Payment Method Selector */}
              <div className="mt-6 pt-4 border-t">
                <Label className="text-sm font-medium mb-3 block">Método de Pago</Label>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-2">
                  <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-50 border-2 border-transparent data-[state=checked]:border-[#009ee3] cursor-pointer" data-state={paymentMethod === 'mercadopago' ? 'checked' : ''}>
                    <RadioGroupItem value="mercadopago" id="mp" />
                    <Label htmlFor="mp" className="flex items-center gap-3 cursor-pointer flex-1">
                      <div className="w-10 h-10 bg-[#009ee3] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">MP</span>
                      </div>
                      <div>
                        <p className="font-medium">Mercado Pago</p>
                        <p className="text-xs text-slate-500">Tarjeta, OXXO, SPEI, y más</p>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-50 border-2 border-transparent data-[state=checked]:border-[#635bff] cursor-pointer" data-state={paymentMethod === 'stripe' ? 'checked' : ''}>
                    <RadioGroupItem value="stripe" id="stripe" />
                    <Label htmlFor="stripe" className="flex items-center gap-3 cursor-pointer flex-1">
                      <div className="w-10 h-10 bg-[#635bff] rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">Tarjeta de Crédito</p>
                        <p className="text-xs text-slate-500">Visa, Mastercard, Amex</p>
                      </div>
                    </Label>
                  </div>
                  {deliveryType === 'pickup' && (
                    <div className="flex items-center space-x-3 p-3 rounded-xl bg-green-50 border-2 border-transparent data-[state=checked]:border-green-500 cursor-pointer" data-state={paymentMethod === 'cash' ? 'checked' : ''}>
                      <RadioGroupItem value="cash" id="cash" />
                      <Label htmlFor="cash" className="flex items-center gap-3 cursor-pointer flex-1">
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                          <span className="text-white text-lg">💵</span>
                        </div>
                        <div>
                          <p className="font-medium">Pagar en Farmacia</p>
                          <p className="text-xs text-slate-500">Efectivo o tarjeta al recoger</p>
                        </div>
                      </Label>
                    </div>
                  )}
                </RadioGroup>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={loading}
                className={`w-full mt-6 ${paymentMethod === 'cash' ? 'bg-green-500 hover:bg-green-600' : 'btn-primary'}`}
                data-testid="checkout-btn"
              >
                {loading ? 'Procesando...' : paymentMethod === 'cash' ? 'Confirmar Pedido' : 'Proceder al Pago'}
              </Button>

              {!isAuthenticated && (
                <p className="text-xs text-center text-slate-500 mt-2">
                  <button onClick={() => navigate('/login')} className="text-[#2E7DCD] hover:underline">
                    Inicia sesión
                  </button> para continuar
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
